/**
 * ForecastBulk — Bulk-Erstellung von Monats-Forecasts mit AI-Vorschlaegen.
 *
 * @page /forecast/bulk
 * @roles admin, manager
 * @api /api/forecast/bulk-status, /api/forecast/bulk-erstellen, /api/forecast/bulk-historie/:id
 * @api /api/forecast/ai-suggestion
 * @i18n forecastBulk
 * @since v2.60.0
 */
import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { Package, Zap, ChevronDown, ChevronRight, Check, AlertTriangle, TrendingUp, TrendingDown, Minus, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SharedForecastTabs from '../components/SharedForecastTabs';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    let data;
    try { data = await r.json(); } catch { throw new Error(`HTTP ${r.status} (kein JSON)`); }
    if (!r.ok) throw new Error(data.message || data.error || `HTTP ${r.status}`);
    return data;
  });
}

const segColors = { A: '#338edc', B: '#6366F1', C: '#F59E0B' };

function fmt(n) {
  if (n == null || isNaN(n)) return '–';
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

// Mini bar chart for expanded row
function MiniBarChart({ data }) {
  if (!data || !data.length) return <div className="text-xs text-[var(--text-secondary)]">–</div>;
  const max = Math.max(...data.map(d => d.pakete || 0), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => {
        const h = ((d.pakete || 0) / max) * 100;
        return (
          <div key={i} className="flex flex-col items-center gap-0.5 flex-1">
            <div
              className="w-full rounded-t"
              style={{ height: `${Math.max(h, 2)}%`, backgroundColor: '#338edc', minHeight: 2 }}
            />
            <span className="text-[9px] text-[var(--text-secondary)] whitespace-nowrap">{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ForecastBulk() {
  const { t } = useLanguage();
  const now = new Date();
  const defaultMonth = now.getMonth() + 2 > 12
    ? { monat: (now.getMonth() + 2) % 12 || 12, jahr: now.getFullYear() + 1 }
    : { monat: now.getMonth() + 2, jahr: now.getFullYear() };

  const [monat, setMonat] = useState(defaultMonth.monat);
  const [jahr, setJahr] = useState(defaultMonth.jahr);
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [manualOverrides, setManualOverrides] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [expandedRow, setExpandedRow] = useState(null);
  const [historie, setHistorie] = useState(null);
  const [historieLoading, setHistorieLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [overwriteModal, setOverwriteModal] = useState(null); // { kunde, newValue, quelle, isBulk, bulkItems }
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [aktivFilter, setAktivFilter] = useState('all');
  const [error, setError] = useState(null);
  const [aktionen, setAktionen] = useState([]);

  const monthStr = `${jahr}-${String(monat).padStart(2, '0')}`;
  const monthNames = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api(`/api/forecast/bulk-status?monat=${monat}&jahr=${jahr}`);
      setCustomers(data.customers || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('[ForecastBulk] load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [monat, jahr]);

  useEffect(() => {
    loadStatus();
    setAiSuggestions({});
    setManualOverrides({});
    setSelected(new Set());
    setSubmitResult(null);
    // Load Aktionszeiträume for this month
    const daysInM = new Date(jahr, monat, 0).getDate();
    const von = `${jahr}-${String(monat).padStart(2, '0')}-01`;
    const bis = `${jahr}-${String(monat).padStart(2, '0')}-${String(daysInM).padStart(2, '0')}`;
    api(`/api/aktionszeitraeume?von=${von}&bis=${bis}`).then(setAktionen).catch(() => setAktionen([]));
  }, [loadStatus]);

  const loadAiSuggestions = useCallback(async () => {
    setAiLoading(true);
    setError(null);
    try {
      const data = await api('/api/forecast/ai-suggestion', {
        method: 'POST',
        body: { month: monthStr, segment: 'all' },
      });
      const map = {};
      (data.suggestions || []).forEach(s => {
        map[s.kunde_id] = s;
      });
      setAiSuggestions(map);
    } catch (err) {
      console.error('[ForecastBulk] AI error:', err);
      setError(`AI-Vorschläge: ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  }, [monthStr]);

  const loadHistorie = useCallback(async (kundeId) => {
    setHistorieLoading(true);
    setHistorie(null);
    try {
      const data = await api(`/api/forecast/bulk-historie/${kundeId}?monate=6`);
      setHistorie(data);
    } catch (err) {
      console.error('[ForecastBulk] historie error:', err);
      setError(`Historie: ${err.message}`);
    } finally {
      setHistorieLoading(false);
    }
  }, []);

  const toggleRow = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      setHistorie(null);
    } else {
      setExpandedRow(id);
      loadHistorie(id);
    }
  };

  const getEffective = (c) => {
    if (manualOverrides[c.id] != null && manualOverrides[c.id] !== '') return parseInt(manualOverrides[c.id]);
    if (aiSuggestions[c.id]) return Math.round(aiSuggestions[c.id].pakete);
    return null;
  };

  const filtered = useMemo(() => {
    let list = customers;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q));
    }
    if (segmentFilter !== 'all') {
      list = list.filter(c => c.segment === segmentFilter);
    }
    if (aktivFilter !== 'all') {
      list = list.filter(c => (c.fulfillment_status || 'unbekannt') === aktivFilter);
    }
    return list;
  }, [customers, searchTerm, segmentFilter, aktivFilter]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllOpen = () => {
    const openIds = filtered.filter(c => !c.forecast_id && getEffective(c) > 0).map(c => c.id);
    setSelected(new Set(openIds));
  };

  const submitSingle = async (c, forceOverwrite = false) => {
    const val = getEffective(c);
    if (!val) return;
    const quelle = manualOverrides[c.id] != null && manualOverrides[c.id] !== '' ? 'manuell' : 'ai';

    // Check if customer forecast exists and show warning (before first API call)
    if (!forceOverwrite && c.kundenForecast && ['submitted', 'approved', 'in_review'].includes(c.kundenForecast.status) && val !== c.kundenForecast.pakete) {
      setOverwriteModal({ kunde: c, newValue: val, quelle, isBulk: false });
      return;
    }

    setSubmitting(true);
    try {
      const result = await api('/api/forecast/bulk-erstellen', {
        method: 'POST',
        body: { monat, jahr, forecasts: [{ kunde_id: c.id, pakete_gesamt: val, quelle, force_overwrite: forceOverwrite }] },
      });
      // Check if backend returned CUSTOMER_FORECAST_EXISTS error
      const cfError = result.errors?.find(e => e.error === 'CUSTOMER_FORECAST_EXISTS' && e.kunde_id === c.id);
      if (cfError) {
        setOverwriteModal({ kunde: { ...c, kundenForecast: cfError.kundenForecast, name: cfError.kundeName || c.name }, newValue: val, quelle, isBulk: false });
        return;
      }
      await loadStatus();
    } catch (err) {
      console.error('[ForecastBulk] submit error:', err);
      setError(`Übernehmen: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const submitBulk = async (forceOverwrite = false) => {
    setConfirmModal(false);
    setSubmitting(true);
    try {
      const items = [...selected].map(id => {
        const c = customers.find(x => x.id === id);
        const val = getEffective(c);
        const quelle = manualOverrides[id] != null && manualOverrides[id] !== '' ? 'manuell' : 'ai';
        return { kunde_id: id, pakete_gesamt: val, quelle, force_overwrite: forceOverwrite };
      }).filter(x => x.pakete_gesamt > 0);

      const result = await api('/api/forecast/bulk-erstellen', {
        method: 'POST',
        body: { monat, jahr, forecasts: items },
      });

      // Check for CUSTOMER_FORECAST_EXISTS errors
      const cfErrors = (result.errors || []).filter(e => e.error === 'CUSTOMER_FORECAST_EXISTS');
      if (cfErrors.length > 0 && !forceOverwrite) {
        // Show overwrite modal for all affected customers
        setOverwriteModal({ isBulk: true, bulkItems: cfErrors, allItems: items });
        setSubmitting(false);
        return;
      }

      setSubmitResult(result);
      setSelected(new Set());
      await loadStatus();
    } catch (err) {
      console.error('[ForecastBulk] bulk error:', err);
      setError(`Bulk-Erstellen: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const monthOptions = [];
  for (let y = now.getFullYear(); y <= now.getFullYear() + 1; y++) {
    for (let m = 1; m <= 12; m++) {
      monthOptions.push({ monat: m, jahr: y, label: `${monthNames[m]} ${y}` });
    }
  }

  // Trend: show – when no meaningful data (0 avg or missing last month)
  const TrendBadge = ({ pct, avg3m, lastMonth }) => {
    if (!avg3m || lastMonth == null || lastMonth === 0) return <span className="text-xs text-[var(--text-secondary)]">–</span>;
    if (pct > 5) return <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-600"><TrendingUp size={12} />+{pct}%</span>;
    if (pct < -5) return <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-500"><TrendingDown size={12} />{pct}%</span>;
    return <span className="inline-flex items-center gap-0.5 text-xs font-medium text-[var(--text-secondary)]"><Minus size={12} />~0%</span>;
  };

  const SegBadge = ({ seg }) => (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[9px] font-bold"
      style={{ backgroundColor: segColors[seg] || '#9CA3AF' }}>{seg}</span>
  );

  // Confidence as dots: ●●● high, ●● medium, ● low
  const ConfidenceDots = ({ level }) => {
    const map = { high: { dots: '●●●', cls: 'text-emerald-500' }, medium: { dots: '●●', cls: 'text-amber-500' }, low: { dots: '●', cls: 'text-red-400' } };
    const c = map[level] || map.low;
    return <span className={`text-[10px] ${c.cls}`} title={level}>{c.dots}</span>;
  };

  const FulfillmentBadge = ({ status }) => {
    const cfg = { aktiv: { bg: '#D1FAE5', text: '#065F46' }, ruhend: { bg: '#FEF3C7', text: '#78350F' }, inaktiv: { bg: '#F3F4F6', text: '#374151' } };
    const c = cfg[status] || cfg.inaktiv;
    const labels = { aktiv: t('customers.fulfillmentAktiv'), ruhend: t('customers.fulfillmentRuhend'), inaktiv: t('customers.fulfillmentInaktiv') };
    return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: c.bg, color: c.text }}>{labels[status] || status}</span>;
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      entwurf: 'bg-amber-50 text-amber-700',
      verteilt: 'bg-emerald-50 text-emerald-700',
      freigegeben: 'bg-blue-50 text-blue-700',
      bestaetigt: 'bg-emerald-50 text-emerald-700',
    };
    return status ? <span className={`text-[10px] px-1.5 py-0.5 rounded ${styles[status] || 'bg-gray-100 text-[var(--text-secondary,#4B5563)]'}`}>{status}</span> : null;
  };

  // Source badge: shows where the forecast comes from
  const SourceBadge = ({ vorschlag_basis, kundenForecast }) => {
    if (kundenForecast && kundenForecast.status === 'ueberschrieben') {
      return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-amber-50 text-amber-700">{t('forecast.overwritten')}</span>;
    }
    if (kundenForecast && ['submitted', 'approved', 'in_review'].includes(kundenForecast.status)) {
      return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#E6F1FB] text-[#185FA5]">{t('forecast.fromCustomerValue', { pakete: (kundenForecast.pakete || 0).toLocaleString() })}</span>;
    }
    if (vorschlag_basis === 'kunde') {
      return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-[#E6F1FB] text-[#185FA5]">{t('forecast.accepted')}</span>;
    }
    if (vorschlag_basis) {
      return <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-[var(--text-secondary,#4B5563)]">{t('forecast.fromIntern')}</span>;
    }
    return null;
  };

  return (
    <div className="p-6 space-y-6">
      <SharedForecastTabs />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">{t('forecastBulk.title')}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">{t('forecastBulk.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={`${jahr}-${monat}`}
            onChange={(e) => { const [y, m] = e.target.value.split('-').map(Number); setJahr(y); setMonat(m); }}
            className="px-3 py-2 border border-[var(--border-primary)] rounded-lg text-sm bg-[var(--bg-primary)] text-[var(--text-primary)]"
          >
            {monthOptions.map(o => (
              <option key={`${o.jahr}-${o.monat}`} value={`${o.jahr}-${o.monat}`}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={loadAiSuggestions}
            disabled={aiLoading}
            className="flex items-center gap-2 px-4 py-2 bg-[#338edc] text-white rounded-lg text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
          >
            {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {t('forecastBulk.generateAi')}
          </button>
          <button
            onClick={() => selected.size > 0 && setConfirmModal(true)}
            disabled={selected.size === 0 || submitting}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {t('forecastBulk.submitAll')} ({selected.size})
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: t('forecastBulk.kpi.activeCustomers'), value: summary.total_active, icon: <Package size={18} className="text-[#338edc]" /> },
            { label: t('forecastBulk.kpi.hasForecast'), value: summary.has_forecast, icon: <Check size={18} className="text-emerald-500" /> },
            { label: t('forecastBulk.kpi.stillOpen'), value: summary.still_open, icon: <AlertTriangle size={18} className="text-amber-500" /> },
            { label: t('forecastBulk.kpi.hasData'), value: summary.has_data, icon: <TrendingUp size={18} className="text-[#6366F1]" /> },
          ].map((kpi, i) => (
            <div key={i} className="border border-[var(--border-primary)] rounded-[16px] p-4 bg-[var(--bg-primary)]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[var(--text-secondary)]">{kpi.label}</span>
                {kpi.icon}
              </div>
              <span className="text-2xl font-semibold text-[var(--text-primary)]">{kpi.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Aktionszeiträume Warning */}
      {aktionen.length > 0 && (() => {
        const affectedKunden = new Set(aktionen.map(a => a.kunde_id));
        return (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 flex items-center gap-2">
            <Zap size={16} className="text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-700">
              {t('forecastPromo.bulkWarning', { count: affectedKunden.size })}
            </span>
            <div className="flex gap-1.5 ml-2 flex-wrap">
              {aktionen.slice(0, 5).map(a => {
                const typColors = { black_week: '#EF4444', sale: '#F59E0B', launch: '#3B82F6', kampagne: '#8B5CF6', saison: '#06B6D4', sonstige: '#6B7280' };
                return (
                  <span key={a.id} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (typColors[a.typ] || '#6B7280') + '20', color: typColors[a.typ] || '#6B7280' }}>
                    {a.kunde_name}: {a.titel} {a.multiplikator ? `${a.multiplikator}x` : `+${a.zusatz_orders}`}
                  </span>
                );
              })}
              {aktionen.length > 5 && <span className="text-xs text-amber-600">+{aktionen.length - 5}</span>}
            </div>
          </div>
        );
      })()}

      {/* Error toast */}
      {error && (
        <div className="border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="text-red-600 text-sm hover:underline ml-4">✕</button>
        </div>
      )}

      {/* Submit result banner */}
      {submitResult && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-emerald-700">
            {t('forecastBulk.resultMessage', { created: submitResult.created, distributed: submitResult.distributed, errors: submitResult.errors?.length || 0 })}
          </span>
          <button onClick={() => setSubmitResult(null)} className="text-emerald-600 text-sm hover:underline">OK</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder={t('forecastBulk.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-2 border border-[var(--border-primary)] rounded-lg text-sm w-64 bg-[var(--bg-primary)] text-[var(--text-primary)]"
        />
        <div className="flex gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-primary)]">
          {['all', 'A', 'B', 'C'].map(seg => (
            <button
              key={seg}
              onClick={() => setSegmentFilter(seg)}
              className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                segmentFilter === seg ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {seg === 'all' ? t('forecastBulk.allSegments') : `${seg}-${t('forecastBulk.segCustomers')}`}
            </button>
          ))}
        </div>
        <select
          value={aktivFilter}
          onChange={e => setAktivFilter(e.target.value)}
          className="px-3 py-2 border border-[var(--border-primary)] rounded-lg text-xs bg-[var(--bg-primary)] text-[var(--text-primary)]"
        >
          <option value="all">{t('customers.activityStatus')}: {t('customers.alle')}</option>
          <option value="aktiv">{t('customers.fulfillmentAktiv')}</option>
          <option value="ruhend">{t('customers.fulfillmentRuhend')}</option>
          <option value="inaktiv">{t('customers.fulfillmentInaktiv')}</option>
        </select>
        <button onClick={selectAllOpen} className="text-xs text-[#338edc] hover:underline ml-auto">
          {t('forecastBulk.selectAllOpen')}
        </button>
      </div>

      {/* Table — reduced columns: ☐ | Kunde+Seg | Ø3M | Trend | FC/AI | Manuell | Aktion */}
      <div className="border border-[var(--border-primary)] rounded-[16px] overflow-hidden bg-[var(--bg-primary)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border-primary)]">
              <th className="w-8 px-2 py-3 text-left">
                <input type="checkbox"
                  onChange={(e) => e.target.checked ? selectAllOpen() : setSelected(new Set())}
                  checked={selected.size > 0 && selected.size === filtered.filter(c => !c.forecast_id && getEffective(c) > 0).length}
                />
              </th>
              <th className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-medium">{t('forecastBulk.col.customer')}</th>
              <th className="px-2 py-3 text-right text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-medium w-20">{t('forecastBulk.col.avg3m')}</th>
              <th className="px-2 py-3 text-center text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-medium w-16">{t('forecastBulk.col.trend')}</th>
              <th className="px-2 py-3 text-right text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-medium w-28">{t('forecastBulk.col.currentForecast')} / AI</th>
              <th className="px-2 py-3 text-right text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-medium w-24">{t('forecastBulk.col.manual')}</th>
              <th className="px-2 py-3 text-center text-[11px] uppercase tracking-wider text-[var(--text-secondary)] font-medium w-24">{t('forecastBulk.col.action')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-[var(--text-secondary)]">
                <Loader2 size={20} className="animate-spin inline mr-2" />{t('forecastBulk.loading')}
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-[var(--text-secondary)]">{t('forecastBulk.noCustomers')}</td></tr>
            ) : filtered.map(c => {
              const ai = aiSuggestions[c.id];
              const effective = getEffective(c);
              const isOpen = !c.forecast_id;
              const isExpanded = expandedRow === c.id;

              return (
                <Fragment key={c.id}>
                  <tr
                    onClick={(e) => { if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'BUTTON' && !e.target.closest('button')) toggleRow(c.id); }}
                    className={`border-b border-[var(--border-primary)] hover:bg-[var(--bg-hover,#F9FAFB)] transition-colors cursor-pointer ${!isOpen ? 'opacity-60' : ''}`}
                  >
                    <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} disabled={!isOpen || !effective} />
                    </td>
                    {/* Kunde + Segment + Fulfillment inline */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronDown size={14} className="text-[var(--text-secondary)] shrink-0" /> : <ChevronRight size={14} className="text-[var(--text-secondary)] shrink-0" />}
                        <SegBadge seg={c.segment} />
                        <span className="font-medium text-[var(--text-primary)] truncate">{c.name}</span>
                        <FulfillmentBadge status={c.fulfillment_status} />
                      </div>
                    </td>
                    {/* Ø 3M */}
                    <td className="px-2 py-2.5 text-right font-mono text-[var(--text-primary)] text-xs">{c.avg_pakete_3m > 0 ? fmt(c.avg_pakete_3m) : '–'}</td>
                    {/* Trend */}
                    <td className="px-2 py-2.5 text-center"><TrendBadge pct={c.trend_pct} avg3m={c.avg_pakete_3m} lastMonth={c.last_month_pakete} /></td>
                    {/* FC / AI combined */}
                    <td className="px-2 py-2.5 text-right">
                      {c.forecast_pakete ? (
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <span className="font-mono text-[var(--text-primary)] text-xs">{fmt(c.forecast_pakete)}</span>
                          <StatusBadge status={c.forecast_status} />
                          <SourceBadge vorschlag_basis={c.vorschlag_basis} kundenForecast={c.kundenForecast} />
                        </div>
                      ) : ai ? (
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <span className="font-mono text-[var(--text-primary)] text-xs">{fmt(ai.pakete)}</span>
                          <ConfidenceDots level={ai.confidence} />
                          {c.kundenForecast && ['submitted', 'approved', 'in_review'].includes(c.kundenForecast.status) && (
                            <SourceBadge kundenForecast={c.kundenForecast} />
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <span className="text-xs text-[var(--text-secondary)]">{aiLoading ? '...' : '–'}</span>
                          {c.kundenForecast && ['submitted', 'approved', 'in_review'].includes(c.kundenForecast.status) && (
                            <SourceBadge kundenForecast={c.kundenForecast} />
                          )}
                        </div>
                      )}
                    </td>
                    {/* Manual input */}
                    <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="number"
                        min="0"
                        placeholder={ai ? String(Math.round(ai.pakete)) : '–'}
                        value={manualOverrides[c.id] ?? ''}
                        onChange={(e) => setManualOverrides(prev => ({ ...prev, [c.id]: e.target.value }))}
                        disabled={!isOpen}
                        className="w-full px-2 py-1 border border-[var(--border-primary)] rounded text-right text-xs font-mono bg-[var(--bg-primary)] text-[var(--text-primary)] disabled:opacity-40"
                      />
                    </td>
                    {/* Action */}
                    <td className="px-2 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                      {isOpen && effective > 0 ? (
                        <button
                          onClick={() => submitSingle(c)}
                          disabled={submitting}
                          className="px-3 py-1 bg-[#338edc] text-white rounded text-xs font-medium hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
                        >
                          {t('forecastBulk.apply')}
                        </button>
                      ) : c.forecast_id ? (
                        <Check size={14} className="inline text-emerald-600" />
                      ) : null}
                    </td>
                  </tr>
                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-inset,#F8F9FC)]">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-3 gap-6">
                          {/* Mini chart */}
                          <div>
                            <h4 className="text-xs font-medium text-[var(--text-secondary)] uppercase mb-2">{t('forecastBulk.detail.history')}</h4>
                            {historieLoading ? (
                              <Loader2 size={16} className="animate-spin text-[var(--text-secondary)]" />
                            ) : historie?.historie ? (
                              <MiniBarChart data={historie.historie.map(h => ({
                                pakete: parseInt(h.pakete) || 0,
                                label: h.monat?.substring(5, 7) || '',
                              }))} />
                            ) : <span className="text-xs text-[var(--text-secondary)]">–</span>}
                          </div>
                          {/* AI info */}
                          <div>
                            <h4 className="text-xs font-medium text-[var(--text-secondary)] uppercase mb-2">{t('forecastBulk.detail.aiInfo')}</h4>
                            {ai ? (
                              <div className="space-y-1 text-xs text-[var(--text-secondary)]">
                                <div>{t('forecastBulk.detail.basis')}: <span className="font-medium text-[var(--text-primary)]">{ai.basis && typeof ai.basis === 'object' ? `Ø${ai.basis.avg_3m}, ${ai.basis.trend}, Saison ${ai.basis.saisonalitaet}` : String(ai.basis || '3M avg')}</span></div>
                                <div>{t('forecastBulk.detail.confidence')}: <ConfidenceDots level={ai.confidence} /> <span className="ml-1">{ai.confidence}</span></div>
                                {ai.reasoning && <div className="text-[var(--text-secondary)]">{ai.reasoning}</div>}
                              </div>
                            ) : <span className="text-xs text-[var(--text-secondary)]">{t('forecastBulk.detail.noAi')}</span>}
                          </div>
                          {/* Forecast history */}
                          <div>
                            <h4 className="text-xs font-medium text-[var(--text-secondary)] uppercase mb-2">{t('forecastBulk.detail.forecasts')}</h4>
                            {historie?.forecasts?.length ? (
                              <div className="space-y-1">
                                {historie.forecasts.slice(0, 4).map(f => (
                                  <div key={f.id} className="flex justify-between text-xs">
                                    <span className="text-[var(--text-secondary)]">{monthNames[f.monat]} {f.jahr}</span>
                                    <span className="font-mono text-[var(--text-primary)]">{fmt(f.pakete_gesamt)} <StatusBadge status={f.status} /></span>
                                  </div>
                                ))}
                              </div>
                            ) : <span className="text-xs text-[var(--text-secondary)]">–</span>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-primary)] p-6 w-[440px] max-h-[80vh] overflow-auto">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{t('forecastBulk.confirmTitle')}</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              {t('forecastBulk.confirmMessage', { count: selected.size, month: `${monthNames[monat]} ${jahr}` })}
            </p>
            <div className="max-h-48 overflow-auto mb-4 border border-[var(--border-primary)] rounded-lg">
              <table className="w-full text-xs">
                <tbody>
                  {[...selected].map(id => {
                    const c = customers.find(x => x.id === id);
                    return c ? (
                      <tr key={id} className="border-b border-[var(--border-primary)]">
                        <td className="px-3 py-1.5 font-medium text-[var(--text-primary)]">{c.name}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-[var(--text-primary)]">{fmt(getEffective(c))}</td>
                      </tr>
                    ) : null;
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmModal(false)} className="px-4 py-2 border border-[var(--border-primary)] rounded-lg text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]">
                {t('forecastBulk.cancel')}
              </button>
              <button onClick={() => submitBulk(false)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                {t('forecastBulk.confirmSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overwrite Warning Modal */}
      {overwriteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-[var(--bg-primary)] rounded-2xl border border-[var(--border-primary)] p-6 w-[480px] max-h-[80vh] overflow-auto">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-amber-500 shrink-0" />
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">{t('forecast.overwriteTitle') || 'Kunden-Forecast überschreiben?'}</h3>
            </div>
            {overwriteModal.isBulk ? (
              <>
                <p className="text-sm text-[var(--text-secondary)] mb-3">
                  {(overwriteModal.bulkItems || []).length} {t('forecast.overwriteBulkMessage') || 'Kunden haben eigene Forecasts eingereicht. Diese werden überschrieben:'}
                </p>
                <div className="max-h-40 overflow-auto mb-4 border border-[var(--border-primary)] rounded-lg">
                  <table className="w-full text-xs">
                    <tbody>
                      {(overwriteModal.bulkItems || []).map((e, i) => (
                        <tr key={i} className="border-b border-[var(--border-primary)]">
                          <td className="px-3 py-1.5 font-medium text-[var(--text-primary)]">{e.kundeName}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-amber-600">{fmt(e.kundenForecast?.pakete)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                {t('forecast.overwriteConfirm', {
                  name: overwriteModal.kunde?.name || '',
                  pakete: (overwriteModal.kunde?.kundenForecast?.pakete || 0).toLocaleString(),
                  date: overwriteModal.kunde?.kundenForecast?.datum ? new Date(overwriteModal.kunde.kundenForecast.datum).toLocaleDateString('de-DE') : '–',
                  newValue: (overwriteModal.newValue || 0).toLocaleString(),
                })}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setOverwriteModal(null)}
                className="px-4 py-2 border border-[var(--border-primary)] rounded-lg text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]"
              >
                {t('forecastBulk.cancel')}
              </button>
              <button
                onClick={async () => {
                  const modal = overwriteModal;
                  setOverwriteModal(null);
                  if (modal.isBulk) {
                    await submitBulk(true);
                  } else {
                    await submitSingle(modal.kunde, true);
                  }
                }}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
              >
                {t('forecast.overwriteButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
